import {
  headingAnchors,
  type ProjectView,
  type WikiPageThread,
  type WikiPageView,
  type WikiPassageAnchor,
} from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { History } from "lucide-react";
import {
  createContext,
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useMediaQuery } from "@/hooks/use-media-query";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { can, canChange } from "../projects/capabilities.ts";
import { Link, navigate, useSearch, wikiPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { PassageHistoryView } from "./history.tsx";
import { AskAgentFooter, usePageThreads, usePageThreadWrite } from "./PageThreads.tsx";
import { CompanionPane, CompanionSheet, PaneThread } from "./ReviewPane.tsx";
import { entryOfThread, IssueMarker, jumpTo, usePassageHighlights } from "./ReviewReader.tsx";
import { placeAnchors } from "./review-anchors.ts";
import { useReviewPref } from "./review-prefs.ts";
import { SelectionPopover } from "./SelectionPopover.tsx";
import { sectionSlug } from "./selection.ts";

// Passage threads on a published page (wiki-collaboration-ux S4, D2, D15): a count marker at the
// end of each passage with a thread on the version shown, the selection popover in comment mode
// (Comment or Question: nothing on a published page waits on a thread), and the thread itself
// in the review's companion pane beside the reading column, or its bottom sheet below 1024 px or
// docked to the bottom. Only what the server returns is shown: a thread on a version the viewer
// may not read never reaches this view, and a `?thread=` link to one opens the page alone.

/** Where the wiki's layout lets the page put its pane and its sheet (`WikiDocs`). */
export interface CompanionSlots {
  /** Beside the reading column, in the rail's place while the pane is open. */
  side: HTMLElement | null;
  /** Under the reading column: the bottom sheet. */
  bottom: HTMLElement | null;
  /** The pane occupies the side slot: the rail steps aside. */
  setPaneOpen: (open: boolean) => void;
}
export const CompanionSlotsContext = createContext<CompanionSlots | null>(null);

/** The heading prefix of the wiki reading view's section ids. */
const HEADING_PREFIX = "wiki-section";

/** How many people a new thread on the current version reaches; null when unknown. */
function useAudience(projectId: string, pageId: string, enabled: boolean) {
  return useQuery({
    // Outside the wiki's keys: a wiki-wide refresh does not recount it (one read per member).
    queryKey: ["page-thread-audience", projectId, pageId],
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async () =>
      (
        await ok(
          await api.api.projects[":projectId"].wiki.pages[":pageId"].threads.audience.$get({
            param: { projectId, pageId },
          }),
        )
      ).people,
  });
}

/** A page thread as the review's markers and pane read it (they share one anatomy). */
const entryOf = (thread: WikiPageThread) =>
  entryOfThread({
    ...thread,
    changeSetId: "",
    itemId: null,
    forAgent: false,
    agentRevisedAt: null,
  });

type Project = Pick<ProjectView, "capabilities" | "frozenAt"> & { id: string };

/**
 * The page's passage threads: the markers to render inline (`inlineAt`), the selection popover
 * and the open thread's pane or sheet (`overlay`). `container` holds the rendered article;
 * `omitBlock` is the title heading the article leaves out.
 */
export function usePagePassages(
  project: Project,
  page: WikiPageView,
  container: RefObject<HTMLElement | null>,
  omitBlock: number | null,
  /** Changes when the article re-renders the same Markdown (its marks shown, then hidden). */
  renderKey: unknown = null,
) {
  const { t } = useTranslation();
  const slots = useContext(CompanionSlotsContext);
  const threads = usePageThreads(project, page.id);
  const write = usePageThreadWrite(project.id, page.id);
  const { userId } = useSession();
  const search = useSearch();
  const threadParam = search.get("thread");
  const wide = useMediaQuery("(min-width: 1024px)");
  const [dockPref, setDockPref] = useReviewPref("dock", userId, ["side", "bottom"] as const);
  const [openId, setOpenId] = useState<string | null>(null);
  // The passage whose history the pane shows (S5, D17); a thread and a history never share it.
  const [historyState, setHistoryState] = useState<{
    key: string;
    anchor: WikiPassageAnchor;
  } | null>(null);
  // Held for the page and version it was opened on: another one opens without it, derived here
  // so no request goes out for an anchor of the previous page.
  const shownKey = `${page.id}:${page.shownVersion}`;
  const historyOf = historyState?.key === shownKey ? historyState.anchor : null;
  const setHistoryOf = (anchor: WikiPassageAnchor | null) =>
    setHistoryState(anchor ? { key: shownKey, anchor } : null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState(true);
  const opener = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const focusPane = useRef(false);
  const handled = useRef<string | null>(null);
  const available = !!slots && can(project, "wiki.comment") && !threads.isError;
  const isCurrent = page.shownVersion === page.version;
  const mayComment = available && isCurrent && canChange(project, "wiki.comment");
  const audience = useAudience(project.id, page.id, mayComment);
  // Any reader may read a passage's history, on any version they open (S5).
  const historyAvailable = !!slots;
  const all = threads.data ?? [];
  // Passages of the version shown; a detached thread lists in Discussion only.
  const placedThreads = available
    ? all.filter(
        (th) => !th.detached && th.anchoredVersion === page.shownVersion && !!th.anchor?.quote,
      )
    : [];
  const anchors = placedThreads.map((th) => ({
    quote: th.anchor?.quote ?? "",
    prefix: th.anchor?.prefix ?? "",
    suffix: th.anchor?.suffix ?? "",
  }));
  const placements = anchors.length ? placeAnchors(page.markdown, anchors, [], omitBlock) : [];
  const open = available ? (all.find((th) => th.id === openId) ?? null) : null;
  const openIndex = open ? placedThreads.findIndex((th) => th.id === open.id) : -1;
  const threadAnchor = openIndex >= 0 ? (anchors[openIndex] ?? null) : null;
  const history = historyAvailable ? historyOf : null;
  const openAnchor = history
    ? { quote: history.quote, prefix: history.prefix, suffix: history.suffix }
    : threadAnchor;
  usePassageHighlights(
    container,
    { threads: anchors, flags: [] },
    openAnchor,
    `${page.markdown}\u0000${String(renderKey)}`,
  );

  const openThread = (id: string, from?: HTMLElement | null) => {
    opener.current = from ?? (document.activeElement as HTMLElement | null);
    setHistoryOf(null);
    setOpenId(id);
    setExpanded(true);
    focusPane.current = true;
  };
  const openHistory = (anchor: WikiPassageAnchor, from?: HTMLElement | null) => {
    // The thread's own opener (its marker) stays the place focus returns to: the History button
    // inside the thread's pane goes with it.
    opener.current = opener.current?.isConnected
      ? opener.current
      : (from ?? (document.activeElement as HTMLElement | null));
    setOpenId(null);
    setHistoryOf(anchor);
    setExpanded(true);
    focusPane.current = true;
  };
  const close = () => {
    setOpenId(null);
    setHistoryOf(null);
    const back = opener.current;
    opener.current = null;
    if (back?.isConnected) {
      back.focus();
    }
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  // A thread link (web-shell "Thread deep links"): the version it sits on, then the thread open
  // at its passage; a thread the viewer may not see is not in the list, and nothing says so.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per link and load.
  useEffect(() => {
    if (!threadParam || !threads.data || handled.current === threadParam) {
      return;
    }
    const target = threads.data.find((th) => th.id === threadParam);
    if (!target) {
      handled.current = threadParam;
      return;
    }
    if (!target.detached && target.anchoredVersion !== page.shownVersion) {
      const version = target.anchoredVersion === page.version ? undefined : target.anchoredVersion;
      const path = wikiPath(project.id, page.id, version);
      navigate(`${path}${path.includes("?") ? "&" : "?"}thread=${encodeURIComponent(target.id)}`, {
        replace: true,
      });
      return;
    }
    handled.current = threadParam;
    setOpenId(target.id);
    focusPane.current = true;
    const at = all.findIndex((th) => th.id === target.id);
    const anchor = target.anchor?.quote
      ? { quote: target.anchor.quote, prefix: target.anchor.prefix, suffix: target.anchor.suffix }
      : null;
    if (at >= 0 && anchor) {
      requestAnimationFrame(() => jumpTo(container.current, anchor));
    }
  }, [threadParam, threads.data, page.shownVersion]);

  // Focus moves into the pane when it opens; Escape closes it from anywhere on the page unless a
  // dialog or the selection popover holds focus (D14), and focus returns to its opener.
  useEffect(() => {
    if (focusPane.current && (openId || historyOf)) {
      focusPane.current = false;
      titleRef.current?.focus();
    }
  });
  const paneShown = !!openId || !!historyOf;
  useEffect(() => {
    if (!paneShown) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (
        e.key === "Escape" &&
        !e.defaultPrevented &&
        !target?.closest("[role='dialog'], [role='menu'], [data-popover], [data-resolve-step]") &&
        !document.querySelector("[role='dialog'][data-state='open']")
      ) {
        e.preventDefault();
        closeRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paneShown]);

  const layout: "side" | "sheet" = wide && dockPref !== "bottom" ? "side" : "sheet";
  const paneOpen = (!!open || !!history) && layout === "side";
  const setPaneOpen = slots?.setPaneOpen;
  useEffect(() => {
    setPaneOpen?.(paneOpen);
    return () => setPaneOpen?.(false);
  }, [paneOpen, setPaneOpen]);

  const inlineAt = new Map<number, ReactNode>();
  // A thread quoting a heading's words on that heading is the heading control's (its count and
  // dialog): no second marker inside the heading.
  const headingText = new Map(
    headingAnchors(page.markdown).map((h) => [h.block, h.text.trim()] as const),
  );
  placedThreads.forEach((th, i) => {
    const at = placements[i];
    if (at?.at == null) {
      return;
    }
    const heading = headingText.get(at.block);
    if (heading !== undefined && heading.startsWith(th.anchor?.quote?.trim() ?? "\u0000")) {
      return;
    }
    const entry = entryOf(th);
    const marker = (
      <IssueMarker
        key={th.id}
        entry={entry}
        open={open?.id === th.id}
        onOpen={() => openThread(th.id)}
      />
    );
    const before = inlineAt.get(at.at);
    inlineAt.set(
      at.at,
      before ? (
        <>
          {before}
          {marker}
        </>
      ) : (
        marker
      ),
    );
  });

  const sections = headingAnchors(page.markdown);
  const sectionOf = (th: WikiPageThread) =>
    th.anchor?.section ? (sections.find((h) => h.slug === th.anchor?.section)?.text ?? null) : null;
  const visibleLine =
    typeof audience.data === "number"
      ? t("wiki.pageThreads.visibleTo", { count: audience.data })
      : t("wiki.pageThreads.visibleToReaders");
  const jump = (anchor: { quote: string; prefix: string; suffix: string } | null) =>
    anchor
      ? () => {
          if (layout === "sheet") {
            setExpanded(false);
          }
          jumpTo(container.current, anchor);
        }
      : null;
  const body = history ? (
    <div data-esi-content="wiki">
      <PassageHistoryView
        key={`${history.quote}:${history.prefix}:${history.suffix}`}
        projectId={project.id}
        pageId={page.id}
        version={page.shownVersion}
        anchor={history}
        section={
          history.section ? (sections.find((h) => h.slug === history.section)?.text ?? null) : null
        }
        pageTitle={page.title}
        titleRef={titleRef}
        onJump={jump(openAnchor)}
      />
    </div>
  ) : open ? (
    <div data-esi-content="wiki">
      <PaneThread
        key={open.id}
        thread={open}
        blocking={false}
        quote={open.anchor?.quote ?? null}
        section={sectionOf(open)}
        pageTitle={page.title}
        onJump={jump(threadAnchor)}
        draft={drafts[open.id] ?? ""}
        onDraft={(text) => setDrafts((d) => ({ ...d, [open.id]: text }))}
        canComment={canChange(project, "wiki.comment")}
        titleRef={titleRef}
        busy={write.isPending}
        error={write.isError ? write.error.message : null}
        visibleLine={visibleLine}
        whoResolves={t("wiki.pageThreads.whoResolves")}
        onReply={(text) => write.mutateAsync({ kind: "reply", threadId: open.id, body: text })}
        onResolve={(note) => write.mutateAsync({ kind: "resolve", threadId: open.id, note })}
        onReopen={() => write.mutateAsync({ kind: "reopen", threadId: open.id })}
        onDelete={(commentId) =>
          write.mutateAsync({ kind: "delete", threadId: open.id, commentId })
        }
        extra={
          <>
            {open.anchor?.quote && !open.detached && open.anchoredVersion === page.shownVersion ? (
              <Button
                type="button"
                size="xs"
                variant="ghost"
                className="self-start"
                onClick={(e) => {
                  const anchor = open.anchor;
                  if (anchor) {
                    openHistory(anchor, e.currentTarget);
                  }
                }}
              >
                <History aria-hidden="true" strokeWidth={1.75} />
                {t("wiki.history.passage.title")}
              </Button>
            ) : null}
            {open.detached || open.anchoredVersion !== page.shownVersion ? (
              <p className="text-xs text-fg-muted">
                {t("wiki.pageThreads.detached")}{" "}
                <Link
                  href={wikiPath(project.id, page.id, open.anchoredVersion)}
                  className="text-accent-text underline underline-offset-4"
                >
                  {t("wiki.pageThreads.onVersion", { version: open.anchoredVersion })}
                </Link>
              </p>
            ) : null}
          </>
        }
        footer={<AskAgentFooter project={project} pageId={page.id} thread={open} />}
      />
    </div>
  ) : null;
  const kind = history
    ? t("wiki.history.passage.title")
    : open
      ? t(`wiki.threads.kind.${open.kind}`)
      : "";
  const shownInPane = !!open || !!history;
  const frame = {
    step: null,
    onStep: () => {},
    onClose: close,
    dockable: wide,
    onDock: (dock: "side" | "bottom") => {
      setDockPref(dock);
      focusPane.current = true;
    },
  };
  const overlay =
    !(available || historyAvailable) || !slots
      ? null
      : layout === "side"
        ? shownInPane && slots.side
          ? createPortal(
              <CompanionPane
                {...frame}
                dock="side"
                kind={kind}
                label={history ? t("wiki.history.passage.title") : t("wiki.pageThreads.paneLabel")}
              >
                {body}
              </CompanionPane>,
              slots.side,
            )
          : null
        : slots.bottom
          ? createPortal(
              <CompanionSheet
                {...frame}
                dock="bottom"
                open={shownInPane}
                kind={kind}
                label={history ? t("wiki.history.passage.title") : t("wiki.pageThreads.paneLabel")}
                openCount={0}
                onOpenFirst={() => {}}
                expanded={expanded}
                onExpanded={setExpanded}
                bar={null}
              >
                {body}
              </CompanionSheet>,
              slots.bottom,
            )
          : null;
  const popover =
    mayComment || historyAvailable ? (
      <SelectionPopover
        key={`${page.id}:${page.shownVersion}`}
        mode="comment"
        container={container}
        agentEligible={false}
        kinds={mayComment ? ["comment", "question"] : []}
        onHistory={(passage) =>
          openHistory({
            quote: passage.quote,
            prefix: passage.prefix,
            suffix: passage.suffix,
            section: sectionSlug(passage, HEADING_PREFIX),
          })
        }
        visibleLine={visibleLine}
        onComment={async (passage, kind, text) => {
          const before = new Set(all.map((th) => th.id));
          const after = await write.mutateAsync({
            kind: "open",
            request: {
              kind: kind === "question" ? "question" : "comment",
              body: text,
              anchor: {
                quote: passage.quote,
                prefix: passage.prefix,
                suffix: passage.suffix,
                section: sectionSlug(passage, HEADING_PREFIX),
              },
            },
          });
          const created = after.find((th) => !before.has(th.id));
          if (created) {
            openThread(created.id);
          }
        }}
      />
    ) : null;
  return {
    inlineAt: inlineAt.size ? inlineAt : undefined,
    popover,
    overlay,
    error: write.isError && !open ? write.error.message : null,
  };
}
